/* eslint-disable react-hooks/exhaustive-deps */
import { faCirclePlus, faTrash } from "@fortawesome/free-solid-svg-icons";
import { FieldArray, FormikProps, getIn, useFormikContext } from "formik";
import React, { useEffect, useState } from "react";
import { Button, Card, Col, Row } from "react-bootstrap";

import { OthersIncDtlExemptIncome } from "../../../../config/filingConstants";
import { IExemptIncomes } from "../../../../config/interfaces";
import { UtilService } from "../../../../services";
import FASvgIcon from "../../../FASvgIcon";
import PreventEditComponent from "../../../PreventEditComponent";
import PriceDisplay from "../../../PriceDisplay";
import { NumericInputField, SelectInputField, TextInputField } from "../../../fields";

const OIExemptIncomeFromAnyOtherForm: React.FC = () => {
  const { values } = useFormikContext<IExemptIncomes>();

  const [sum, setSum] = useState(0);
  const [inputTotal, setInputTotal] = useState<{ [key: string]: number }>({});
  const [currentTime, setCurrentTime] = useState<number>(new Date().getTime());

  useEffect(() => {
    let _sum = 0;
    let _totalInput: { [key: string]: number } = {};
    if (values?.OtherExemptIncomes.length) {
      values?.OtherExemptIncomes.forEach((item, index) => {
        _totalInput[index] = parseInt(item[1]) || 0;
        if (item.length && item[1]) {
          _sum += parseInt(item[1]) || 0;
        }
      });
      setInputTotal(() => _totalInput);
    }
    setSum(() => _sum);
  }, [currentTime]);

  const handleAmountInc = (amt: number, index: string) => {
    inputTotal[index] = amt;
    setInputTotal(() => inputTotal);
    const _sum = Object.values(inputTotal).reduce((a, b) => Number(a) + Number(b), 0);
    setSum(() => _sum);
  };

  return (
    <>
      <div className="section-title-wrap">
        <h5>Any Other Exempt Income</h5>
      </div>
      <Card className="more-info-card shadow-none mb-5">
        <Card.Header>
          <Card.Title>Exempt Income</Card.Title>
        </Card.Header>
        <Card.Body>
          <section>
            <FieldArray name="OtherExemptIncomes">
              {({ push, replace, remove, form }) => (
                <>
                  {values?.OtherExemptIncomes && values?.OtherExemptIncomes?.length > 0
                    ? values?.OtherExemptIncomes.map((_, index) => (
                        <section className="mb-3 mb-sm-0" key={`exempt-other-${index}`}>
                          <OtherExemptForm
                            index={index}
                            form={form}
                            onRemove={(itemIdx: number) => {
                              if (values.OtherExemptIncomes.length === 1) {
                                replace(itemIdx, ["", "", ""]);
                              } else {
                                remove(itemIdx);
                              }

                              setCurrentTime(new Date().getTime());
                            }}
                            handleAmountInc={handleAmountInc}
                          />
                        </section>
                      ))
                    : null}
                  <PreventEditComponent>
                    <Button
                      variant="link"
                      className="btn-add-link"
                      onClick={() => push(["", "", ""])}
                    >
                      <FASvgIcon icon={faCirclePlus} />
                      Add more
                    </Button>
                  </PreventEditComponent>
                </>
              )}
            </FieldArray>
          </section>
        </Card.Body>
        <Card.Footer>
          <p className="total-content">
            <span className="total-label">Total:</span>
            <span className="total-amount">
              <PriceDisplay price={sum} />
            </span>
          </p>
        </Card.Footer>
      </Card>
    </>
  );
};

export default OIExemptIncomeFromAnyOtherForm;

type OEFormProps = {
  index: number;
  form: FormikProps<IExemptIncomes>;
  onRemove: (itemIdx: number) => void;
  handleAmountInc: (amt: number, index: string) => void;
};

const OtherExemptForm: React.FC<OEFormProps> = (props: OEFormProps) => {
  const { index, onRemove, form, handleAmountInc } = props;

  const { setFieldValue, handleBlur, values, touched, errors } = form;

  return (
    <>
      <Row className="align-items-center">
        <Col sm={4}>
          <SelectInputField
            placeholder="Select other"
            name={`OtherExemptIncomes.${index}[0]`}
            label="Source"
            options={UtilService.convertToArray(OthersIncDtlExemptIncome)}
            value={{
              "label": OthersIncDtlExemptIncome[values.OtherExemptIncomes[index][0]],
              "value": values.OtherExemptIncomes[index][0]
            }}
            onChange={(selectedStatus) => {
              if (selectedStatus) {
                setFieldValue(`OtherExemptIncomes.${index}[0]`, selectedStatus.value);
              } else {
                setFieldValue(`OtherExemptIncomes.${index}[0]`, undefined);
              }
            }}
            onBlur={handleBlur(`OtherExemptIncomes.${index}[0]`)}
            containerClasses={
              Boolean(getIn(errors, `OtherExemptIncomes.${index}[0]`)) &&
              getIn(touched, `OtherExemptIncomes.${index}[0]`)
            }
            errorMsg={getIn(errors, `OtherExemptIncomes.${index}[0]`)}
          />
        </Col>
        <Col sm>
          <TextInputField
            name={`OtherExemptIncomes.${index}[2]`}
            label="Description"
            value={values.OtherExemptIncomes[index][2]}
            onChange={(value) => setFieldValue(`OtherExemptIncomes.${index}[2]`, value)}
            onBlur={() => {
              handleBlur(`OtherExemptIncomes.${index}[2]`);
              setFieldValue(
                `OtherExemptIncomes.${index}[2]`,
                values.OtherExemptIncomes[index][2].trim()
              );
            }}
            isInvalid={
              Boolean(getIn(errors, `OtherExemptIncomes.${index}[2]`)) &&
              getIn(touched, `OtherExemptIncomes.${index}[2]`)
            }
            errorMsg={getIn(errors, `OtherExemptIncomes.${index}[2]`)}
          />
        </Col>
        <Col sm>
          <NumericInputField
            showLeftIcon={true}
            placeholder=""
            name={`OtherExemptIncomes.${index}[1]`}
            label="Amount"
            value={values.OtherExemptIncomes[index][1]}
            onBlur={handleBlur(`OtherExemptIncomes.${index}[1]`)}
            onChange={(value) => {
              setFieldValue(`OtherExemptIncomes.${index}[1]`, value);
              setTimeout(() => {
                handleAmountInc(Number(value), String(index));
              }, 100);
            }}
            isInvalid={
              Boolean(getIn(errors, `OtherExemptIncomes.${index}[1]`)) &&
              getIn(touched, `OtherExemptIncomes.${index}[1]`)
            }
            errorMsg={getIn(errors, `OtherExemptIncomes.${index}[1]`)}
          />
        </Col>
        <PreventEditComponent>
          <Col sm={"auto"} className="ps-0">
            <Button
              variant="transparent"
              className="text-greyDark icon-only d-none d-sm-flex"
              onClick={() => onRemove(index)}
            >
              <FASvgIcon icon={faTrash} />
            </Button>
            <Button
              variant="transparent"
              className="btn-delete-full d-sm-none"
              onClick={() => onRemove(index)}
            >
              <FASvgIcon icon={faTrash} />
              <span>Delete</span>
            </Button>
          </Col>
        </PreventEditComponent>
      </Row>
      {index !== values.OtherExemptIncomes.length - 1 && <hr className="d-block d-sm-none my-4" />}
    </>
  );
};
